import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiJson, apiSend } from "@/api/client";
import { segment, withQuery, type SaveVariables } from "@/api/query";
import { reportKeys, type MoneyTotal } from "@/api/reports";

/*
 * Mirrors api/src/pbo_invoicing/finance/schemas.py. Money and percentages are strings
 * ("280.00", "20.00"). An invoice's split is in the invoice's currency; expenses carry their
 * rupee value, which is what the profit report counts.
 */

/** A payout is paid per invoice to a person; a pool is kept and spent through expenses. */
export type BucketKind = "payout" | "pool";

export const BUCKET_KINDS: { value: BucketKind; label: string }[] = [
  { value: "pool", label: "Pool - kept and spent through expenses" },
  { value: "payout", label: "Payout - paid per invoice to a person" },
];

export type BucketInput = {
  name: string;
  kind: BucketKind;
  default_percent: string;
  default_payee: string;
  purpose: string;
  /** Rupee ceiling for the month; null = share of the planned income. */
  monthly_limit_inr: string | null;
  sort_order: number;
  archived: boolean;
};

export type Bucket = BucketInput & { id: string; version: number };

/** The editable fields of a bucket: the API refuses a body with anything else. */
export function pickBucketInput(bucket: Bucket): BucketInput {
  const { name, kind, default_percent, default_payee, purpose, monthly_limit_inr } = bucket;
  const { sort_order, archived } = bucket;
  return {
    name,
    kind,
    default_percent,
    default_payee,
    purpose,
    monthly_limit_inr,
    sort_order,
    archived,
  };
}

// --------------------------------------------------------------------------- categories

/** What kind of cost a bill is, and the bucket it draws from unless the bill says otherwise. */
export type CategoryInput = {
  name: string;
  default_bucket_id: string;
  sort_order: number;
  archived: boolean;
  /** The expense account its bills are charged to; null = a new account named after it. */
  ledger_account_id: string | null;
};

export type Category = CategoryInput & {
  id: string;
  default_bucket_name: string;
  ledger_account_name: string;
  version: number;
};

export function pickCategoryInput(category: Category): CategoryInput {
  const { name, default_bucket_id, sort_order, archived, ledger_account_id } = category;
  return { name, default_bucket_id, sort_order, archived, ledger_account_id };
}

// --------------------------------------------------------------------------- the tracker's lists

export type SpendPurpose =
  | "new_purchase"
  | "renewal"
  | "upgrade"
  | "running_cost"
  | "salary"
  | "repair"
  | "statutory"
  | "penalty"
  | "travel"
  | "advance"
  | "other";

export const SPEND_PURPOSES: { value: SpendPurpose; label: string }[] = [
  { value: "new_purchase", label: "New purchase (one-off)" },
  { value: "renewal", label: "Renewal / subscription" },
  { value: "upgrade", label: "Upgrade / add-on" },
  { value: "running_cost", label: "Monthly running cost" },
  { value: "salary", label: "Salary / fees" },
  { value: "repair", label: "Repair / maintenance" },
  { value: "statutory", label: "Statutory fee / filing" },
  { value: "penalty", label: "Penalty / late fee" },
  { value: "travel", label: "Travel / client visit" },
  { value: "advance", label: "Advance / deposit" },
  { value: "other", label: "Other" },
];

/** paid by the company | unpaid (nothing has left any account) | paid from someone's pocket */
export type ExpenseStatus = "paid" | "unpaid" | "to_reimburse";

export const EXPENSE_STATUSES: { value: ExpenseStatus; label: string }[] = [
  { value: "paid", label: "Paid" },
  { value: "unpaid", label: "Unpaid - the bill is in, nothing has gone out yet" },
  { value: "to_reimburse", label: "Paid from someone's pocket - to reimburse" },
];

export type ExpenseMethod = "" | "bank_transfer" | "upi" | "card" | "cash" | "cheque" | "other";

export const EXPENSE_METHODS: { value: ExpenseMethod; label: string }[] = [
  { value: "bank_transfer", label: "Bank transfer (NEFT / RTGS / IMPS / SWIFT)" },
  { value: "upi", label: "UPI" },
  { value: "card", label: "Card" },
  { value: "cash", label: "Cash" },
  { value: "cheque", label: "Cheque" },
  { value: "other", label: "Other" },
];

export type BillingCycle = "monthly" | "quarterly" | "half_yearly" | "annual" | "one_time";

export const BILLING_CYCLES: { value: BillingCycle; label: string }[] = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "half_yearly", label: "Half-yearly" },
  { value: "annual", label: "Annual" },
  { value: "one_time", label: "One-time" },
];

export type RenewalState = "renewed" | "overdue" | "due_soon" | "ok";

/** The label of a list value, or the value itself when it is not on the list. */
export function labelOf<T extends string>(
  list: { value: T; label: string }[],
  value: T | null | undefined,
): string {
  if (!value) return "";
  return list.find((item) => item.value === value)?.label ?? value;
}

// --------------------------------------------------------------------------- splits

export type SplitRowInput = { bucket_id: string; percent: string; payee: string };

export type SplitRow = SplitRowInput & {
  bucket_name: string;
  kind: BucketKind;
  planned: string;
  received_share: string;
  paid_out: string;
  balance: string;
};

export type InvoiceSplit = {
  invoice_id: string;
  invoice_no: string | null;
  currency: string;
  total: string;
  received: string;
  /** False while the invoice still follows the bucket defaults. */
  saved: boolean;
  total_percent: string;
  rows: SplitRow[];
};

// --------------------------------------------------------------------------- payouts due

/** Mirrors domain/payouts.py: more than the share paid, all of it, paid before the client's
 *  money came in, some or none of what came in paid; otherwise waiting for the client. */
export type PayoutStatus = "overpaid" | "settled" | "ahead" | "partial" | "due" | "waiting";

/** One bucket's share of one invoice, in the invoice's currency. The rupee figures are at the
 *  rate the invoice achieved, and null while money received has no rupee figure. */
export type PayoutDueRow = {
  invoice_id: string;
  invoice_no: string | null;
  invoice_date: string;
  client_name: string;
  currency: string;
  bucket_id: string;
  bucket_name: string;
  kind: BucketKind;
  payee: string;
  percent: string;
  planned: string;
  released: string;
  paid_out: string;
  /** Released less paid out; negative when paid ahead of the client. */
  pending: string;
  status: PayoutStatus;
  rate_inr: string | null;
  released_inr: string | null;
  paid_out_inr: string;
  pending_inr: string | null;
};

/** What one bucket's payee is owed now over every invoice. */
export type PayoutTotal = {
  bucket_id: string;
  bucket_name: string;
  kind: BucketKind;
  payee: string;
  due: MoneyTotal[];
  due_inr: string;
  not_valued: MoneyTotal[];
  paid_out: MoneyTotal[];
};

/** A bucket's share of the rupees received to date less everything charged to it. */
export type PayoutFund = {
  bucket_id: string;
  name: string;
  kind: BucketKind;
  received_inr: string;
  spent_inr: string;
  fund_inr: string;
};

export type PayoutsDueReport = {
  as_of: string;
  rows: PayoutDueRow[];
  totals: PayoutTotal[];
  funds: PayoutFund[];
  not_converted: MoneyTotal[];
};

// --------------------------------------------------------------------------- expenses

export type ExpenseInput = {
  spent_on: string;
  /** null = the category's default bucket. */
  bucket_id: string | null;
  category_id: string | null;
  /** Set for a payout of that invoice's split, which is then in the invoice's currency. */
  invoice_id: string | null;
  /** Which money account it was paid from; null when not said, and for an unpaid bill. */
  paid_from_id: string | null;
  payee: string;
  description: string;
  purpose: SpendPurpose | null;
  status: ExpenseStatus;
  method: ExpenseMethod;
  currency: string;
  amount: string;
  /** The GST inside the amount. */
  gst_included: string;
  /** TDS the company must deduct, in the bill's currency, and its section ("" = none). */
  tds_section: string;
  tds_amount: string;
  /** Rupees per unit of `currency`; null for a rupee expense. */
  fx_rate: string | null;
  /** A recurring bill, and the period this one covers. */
  billing_cycle: BillingCycle | null;
  period_from: string | null;
  period_to: string | null;
  /** Drive links to the bill and to the proof of payment. */
  bill_url: string;
  proof_url: string;
  reference: string;
  notes: string;
};

export type Expense = Omit<ExpenseInput, "bucket_id"> & {
  id: string;
  bucket_id: string;
  bucket_name: string;
  category_name: string | null;
  invoice_no: string | null;
  inr_amount: string;
  /** When the bill comes round again, and how loudly to say so. */
  next_renewal: string | null;
  renewal: RenewalState | null;
  /** The day the company paid an unpaid bill; the day a personal payment was repaid. */
  paid_on: string | null;
  reimbursed_on: string | null;
  voided_at: string | null;
  voided_by_username: string | null;
  void_reason: string | null;
  created_by_username: string | null;
  version: number;
  created_at: string;
};

/** An unpaid bill was paid (from which account), or a personal payment repaid (date only). */
export type ExpenseStatusInput = { paid_on: string; paid_from_id: string | null };

/** The bill and the payment proof (https links), both always sent; "" clears one. */
export type ExpenseLinksInput = { bill_url: string; proof_url: string };

export type ExpenseFilters = {
  from?: string;
  to?: string;
  bucketId?: string;
  categoryId?: string;
  invoiceId?: string;
  status?: ExpenseStatus;
  /** Only bills with a billing cycle, soonest renewal first. */
  recurring?: boolean;
  includeVoided?: boolean;
};

/** The most expenses one list shows; the API's own ceiling. */
export const EXPENSE_LIMIT = 500;

export const financeKeys = {
  buckets: (includeArchived: boolean) => ["buckets", includeArchived] as const,
  allBuckets: ["buckets"] as const,
  categories: (includeArchived: boolean) => ["expense-categories", includeArchived] as const,
  allCategories: ["expense-categories"] as const,
  split: (invoiceId: string) => ["splits", invoiceId] as const,
  allSplits: ["splits"] as const,
  expenses: (filters: ExpenseFilters) => ["expenses", filters] as const,
  allExpenses: ["expenses"] as const,
  /** Under "reports", so anything that moves money refreshes it. */
  payoutsDue: (includeSettled: boolean) => ["reports", "payouts-due", includeSettled] as const,
};

// --------------------------------------------------------------------------- requests

export function listBuckets(includeArchived = false): Promise<Bucket[]> {
  return apiJson<Bucket[]>(
    withQuery("/allocation-buckets", { include_archived: includeArchived }),
  );
}

export function saveBucket({ input, existing }: SaveVariables<BucketInput>): Promise<Bucket> {
  return existing
    ? apiSend<Bucket>(`/allocation-buckets/${segment(existing.id)}`, "PUT", input, existing.version)
    : apiSend<Bucket>("/allocation-buckets", "POST", input);
}

export function listCategories(includeArchived = false): Promise<Category[]> {
  return apiJson<Category[]>(
    withQuery("/expense-categories", { include_archived: includeArchived }),
  );
}

export function saveCategory({
  input,
  existing,
}: SaveVariables<CategoryInput>): Promise<Category> {
  return existing
    ? apiSend<Category>(
        `/expense-categories/${segment(existing.id)}`,
        "PUT",
        input,
        existing.version,
      )
    : apiSend<Category>("/expense-categories", "POST", input);
}

export function getSplit(invoiceId: string): Promise<InvoiceSplit> {
  return apiJson<InvoiceSplit>(`/invoices/${segment(invoiceId)}/splits`);
}

export function saveSplit(invoiceId: string, rows: SplitRowInput[]): Promise<InvoiceSplit> {
  return apiSend<InvoiceSplit>(`/invoices/${segment(invoiceId)}/splits`, "PUT", { rows });
}

export function listExpenses(filters: ExpenseFilters): Promise<Expense[]> {
  return apiJson<Expense[]>(
    withQuery("/expenses", {
      spent_from: filters.from,
      spent_to: filters.to,
      bucket_id: filters.bucketId,
      category_id: filters.categoryId,
      invoice_id: filters.invoiceId,
      status: filters.status,
      recurring: filters.recurring ? true : undefined,
      include_voided: filters.includeVoided,
      limit: EXPENSE_LIMIT,
    }),
  );
}

export function recordExpense(input: ExpenseInput): Promise<Expense> {
  return apiSend<Expense>("/expenses", "POST", input);
}

export function setExpenseStatus(
  id: string,
  input: ExpenseStatusInput,
  version: number,
): Promise<Expense> {
  return apiSend<Expense>(`/expenses/${segment(id)}/status`, "PUT", input, version);
}

export function setExpenseLinks(
  id: string,
  input: ExpenseLinksInput,
  version: number,
): Promise<Expense> {
  return apiSend<Expense>(`/expenses/${segment(id)}/links`, "PUT", input, version);
}

export function voidExpense(id: string, reason: string, version: number): Promise<Expense> {
  return apiSend<Expense>(`/expenses/${segment(id)}/void`, "POST", { reason }, version);
}

/** Who paid a bill still to be reimbursed: another person account. */
export function setExpensePaidBy(
  id: string,
  paidFromId: string,
  version: number,
): Promise<Expense> {
  return apiSend<Expense>(
    `/expenses/${segment(id)}/paid-by`,
    "PUT",
    { paid_from_id: paidFromId },
    version,
  );
}

export function getPayoutsDue(includeSettled: boolean): Promise<PayoutsDueReport> {
  return apiJson<PayoutsDueReport>(
    withQuery("/reports/payouts-due", { include_settled: includeSettled }),
  );
}

// --------------------------------------------------------------------------- hooks

export function useBuckets(includeArchived = false) {
  return useQuery({
    queryKey: financeKeys.buckets(includeArchived),
    queryFn: () => listBuckets(includeArchived),
  });
}

export function useCategories(includeArchived = false) {
  return useQuery({
    queryKey: financeKeys.categories(includeArchived),
    queryFn: () => listCategories(includeArchived),
  });
}

export function useSplit(invoiceId: string) {
  return useQuery({ queryKey: financeKeys.split(invoiceId), queryFn: () => getSplit(invoiceId) });
}

export function usePayoutsDue(includeSettled: boolean) {
  return useQuery({
    queryKey: financeKeys.payoutsDue(includeSettled),
    queryFn: () => getPayoutsDue(includeSettled),
  });
}

export function useExpenses(filters: ExpenseFilters) {
  return useQuery({ queryKey: financeKeys.expenses(filters), queryFn: () => listExpenses(filters) });
}

/** Money out changes splits, expenses and every report figure at once. */
function useMoneyOutMutation<Variables, Result>(mutationFn: (variables: Variables) => Promise<Result>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () =>
      Promise.all(
        [
          financeKeys.allBuckets,
          financeKeys.allCategories,
          financeKeys.allSplits,
          financeKeys.allExpenses,
          reportKeys.all,
        ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
      ),
  });
}

export function useSaveBucket() {
  return useMoneyOutMutation(saveBucket);
}

export function useSaveCategory() {
  return useMoneyOutMutation(saveCategory);
}

export function useSaveSplit(invoiceId: string) {
  return useMoneyOutMutation((rows: SplitRowInput[]) => saveSplit(invoiceId, rows));
}

export function useRecordExpense() {
  return useMoneyOutMutation(recordExpense);
}

export function useSetExpenseStatus() {
  return useMoneyOutMutation(
    ({ id, input, version }: { id: string; input: ExpenseStatusInput; version: number }) =>
      setExpenseStatus(id, input, version),
  );
}

export function useSetExpenseLinks() {
  return useMoneyOutMutation(
    ({ id, input, version }: { id: string; input: ExpenseLinksInput; version: number }) =>
      setExpenseLinks(id, input, version),
  );
}

export function useSetExpensePaidBy() {
  return useMoneyOutMutation(
    ({ id, paidFromId, version }: { id: string; paidFromId: string; version: number }) =>
      setExpensePaidBy(id, paidFromId, version),
  );
}

export function useVoidExpense() {
  return useMoneyOutMutation(({ id, reason, version }: { id: string; reason: string; version: number }) =>
    voidExpense(id, reason, version),
  );
}
